<template>
  <ContractEditDialog
    v-if="contractToEdit"
    v-model:visible="contractEditDialogVisible"
    :contract="contractToEdit"
    :license-model-id="licenseToEdit?._id || ''"
    :method="contractEditMethod"
    :is-fallback="isFallback"
    @cancelled="handleContractDialogClosed"
    @saved="handleContractDialogSaved" />

  <LicenseModelDrawer
    v-if="licenseToShow"
    v-model:drawer-visibility="showLicenseDrawerVisible"
    v-model:license="licenseToShow"
    :application-id="props.application._id"
    @delete="(license) => handleDeleteLicenseModelClick(license)"
    @edit="(license) => handleEditLicenseModelClick(license)" />

  <!-- Create Dialog  -->
  <LicenseModelCreateDialog
    v-if="licenseCreateDialogVisible"
    v-model:visible="licenseCreateDialogVisible"
    @add="emit('licenses-changed')" />

  <!-- Edit Dialog  -->
  <LicenseModelEditDialog
    v-if="$props.licensesEditable && licenseToEdit"
    v-model:visible="licenseEditDialogVisible"
    v-model:software-license="licenseToEdit"
    @saved="handleLicenseModelSaved"
    @add="emit('licenses-changed')" />

  <!-- Delete Dialog -->
  <DeleteConfirmDialog
    v-if="licenseToDelete"
    v-model:visible="deleteLicenseDialogVisible"
    :title="i18n.t('deleteLicense')"
    :sub-title="i18n.t('licenseModelDeleteConfirmText')"
    @cancel="deleteLicenseDialogVisible = false"
    @delete="handleDeleteLicenseModel" />

  <!-- Header -->
  <div class="flex items-center gap-2 pb-4 pt-2">
    <div>
      <SmInput
        v-model="searchQuery"
        style="width: 250px; margin-right: 1px"
        size="small"
        outline
        :label="
          i18n.t('views.applicationDetails.licenceModelCard.searchPlaceholder')
        " />
    </div>
    <SmButton class="ml-auto" @click="openAddLicense">
      {{ i18n.t('addLicense') }}
    </SmButton>
  </div>

  <!-- Table -->
  <SmTable
    ref="softwareLicenseTable"
    :columns="columns"
    :data="filteredLicenseModelRows"
    expandable
    :default-sorting="{
      by: 'name',
      asc: false,
    }"
    :selectable="selectable"
    key-field="_id"
    :select-disabled="state !== 'selection'"
    :loading="props.loading"
    class="license-table"
    :style="{ minHeight: props.loading ? '100px' : 'auto' }"
    @cell-click="handleCellClick">
    <!-- Auto License Model -->
    <template #automated="{ row }">
      <!-- Automated -->
      <SmTooltip v-if="row.external_id !== null" placement="left">
        <Link2 :size="22" class="text-green" />

        <template #content>
          <div class="max-w-[250px]">
            <h3>{{ i18n.t('automatedLicenseModel') }}</h3>
            <p>{{ i18n.t('automatedLicenseModelTooltip') }}</p>
          </div>
        </template>
      </SmTooltip>
      <!-- Manual -->
      <SmTooltip v-else placement="left">
        <Link2 :size="22" class="text-contrast-muted" />

        <template #content>
          <div class="max-w-[250px]">
            <h3>{{ i18n.t('manualLicenseModel') }}</h3>
            <p>{{ i18n.t('manualLicenseModelTooltip') }}</p>
          </div>
        </template>
      </SmTooltip>
    </template>
    <!-- Name -->
    <template #name="{ row }">
      <div
        class="flex flex-col gap-[0.2rem]"
        :class="{
          'license-model-deleted': licensesToDelete.includes(row._id),
          'license-model-added': licensesToAdd.includes(row._id),
          'license-model': true,
        }">
        {{ row.name }}
        <span v-if="row.subtitle" class="no-data">
          {{ row.subtitle }}
        </span>
      </div>
    </template>

    <template #expand="{ row }">
      <div class="flex flex-col border-t bg-gray-50 dark:bg-gray-800">
        <div class="w-full py-4 pl-16">
          <div
            v-if="
              contractStore.getContract(row._id) &&
              contractStore.getContract(row._id).length > 0
            ">
            <div
              class="mb-2 grid grid-cols-3 gap-4 text-sm font-bold text-gray-600 dark:text-gray-300">
              <div>{{ i18n.t('description') }}</div>
              <div>{{ i18n.t('billingUnit') }}</div>
              <div>{{ i18n.t('price') }}</div>
            </div>
            <div
              v-for="contract in row.filteredContracts"
              :key="contract._id"
              class="contract-row grid grid-cols-3 gap-4 border-t border-gray-200 py-2 last:border-b-0 dark:border-gray-700"
              role="button"
              tabindex="0"
              @click.stop="handleContractRowClick(row, contract)"
              @keyup.enter.stop="handleContractRowClick(row, contract)"
              @keyup.space.prevent.stop="handleContractRowClick(row, contract)">
              <div>
                <TextMultiline
                  v-if="contract.description"
                  :text="contract.description"
                  :show-tool-tip="true" />
                <span v-else class="italic text-gray-500">{{
                  i18n.t('noDescription')
                }}</span>
              </div>
              <div class="flex items-center gap-2">
                {{ i18n.t(`billingUnitValues.${contract.billing_unit}`) }}
                <span v-if="contract.quota">({{ contract.quota }}x)</span>
              </div>
              <div>
                {{ getCorrectedPrice(contract.price, contract.currency) }} /
                {{ i18n.t(`billingPeriodValues.${contract.billing_period}`) }}
              </div>
            </div>
          </div>
          <div v-else>
            <p class="text-gray-500">{{ i18n.t('noPriceInformation') }}</p>
          </div>
        </div>
      </div>
    </template>

    <!-- External name -->
    <template #extName="{ row }">
      <span>{{ row.external_name }}</span>
    </template>

    <!-- Quota Information -->
    <template #quota="{ row }">
      <QuotaProgressBar
        v-if="applicationDetailsStore.quota[row._id]"
        :quota-information="applicationDetailsStore.quota[row._id]" />
      <span v-else class="italic text-muted">{{ i18n.t('noData') }}</span>
    </template>

    <!-- Price -->
    <template #price="{ row }">
      <span v-if="licenseCosts[row._id] !== null">
        {{ getCorrectedPrice(licenseCosts[row._id]?.total_cost ?? 0) }}
      </span>
    </template>

    <!-- Price per User -->
    <template #header-pricePerUser>
      <div class="flex gap-2 align-middle">
        {{ i18n.t('pricePerUser') }}
        <SmTooltip>
          <template #content>{{ i18n.t('pricePerUserTooltip') }}</template>
          <Info :size="16" class="mt-[1px] cursor-help" />
        </SmTooltip>
      </div>
    </template>
    <template #pricePerUser="{ row }">
      <span v-if="licenseCosts[row._id] !== null">
        {{
          getCorrectedPrice(licenseCosts[row._id]?.cost_per_user_assigned ?? 0)
        }}
      </span>
    </template>

    <!-- Actions -->
    <template #action="{ row }">
      <SmDropdown trigger="click" size="small">
        <SmDropdownItem :icon="Eye" @click="handleShowLicenseModelClick(row)">
          {{ i18n.t('showDetails') }}
        </SmDropdownItem>
        <SmDropdownItem
          :icon="PencilLine"
          @click="handleEditLicenseModelClick(row)">
          {{ i18n.t('edit') }}
        </SmDropdownItem>
        <SmDropdownItem
          hover-color="var(--sm-magenta)"
          :icon="Trash"
          @click="handleDeleteLicenseModelClick(row)">
          {{ i18n.t('delete') }}
        </SmDropdownItem>
      </SmDropdown>
    </template>
  </SmTable>
</template>

<script setup lang="ts">
  import { ComponentExposed } from 'vue-component-type-helpers'
  import { useI18n } from 'vue-i18n'

  import {
    Account,
    ApiError,
    LicenceOut,
    AccountStatus,
    LicenceUpdateIn,
    PriceInformation,
    SoftwareLicense,
    SoftwareLicenseCost,
    SoftwareLicenseMini_Input,
    SoftwareLicenseMiniUpdate,
    SoftwareOut,
  } from '@/client'
  import { deleteSoftwareLicense } from '@/utils/license'

  import { useRoute } from 'vue-router'
  import { Column } from '@/components/sm/SmTable.types'
  import SmTable from '@/components/sm/SmTable.vue'
  import { useApplicationDetailsStore } from '@/stores/ApplicationDetailsView.store'
  import { AccountStore } from '@/stores/account/account.store'
  import { Eye, Info, Link2, PencilLine, Trash } from 'lucide-vue-next'
  import { SoftwareLicensesService } from '@/client'
  import { getCorrectedPrice } from '@/utils/formatter/price'
  import { useContractStore } from '@/stores/contractStore'
  import { usePreferenceStore } from '@/stores/preferenceStore'
  import ContractEditDialog from '@/components/contracts/ContractEditDialog.vue'

  interface Result {
    status: 'success' | 'error'
    message: string
    response?: LicenceOut
  }

  const i18n = useI18n()
  const applicationDetailsStore = useApplicationDetailsStore()
  const contractStore = useContractStore()
  const preferenceStore = usePreferenceStore()

  const licenseToEdit = ref<SoftwareLicense | null>()
  const licenseToDelete = ref<SoftwareLicense | null>(null)
  const licenseToShow = ref<SoftwareLicense | null>(null)

  const licenseCreateDialogVisible = ref(false)
  const licenseEditDialogVisible = ref(false)
  const contractEditDialogVisible = ref(false)
  const deleteLicenseDialogVisible = ref(false)
  const showLicenseDrawerVisible = ref(false)

  const softwareLicenseTable =
    ref<ComponentExposed<typeof SmTable<SoftwareLicense>>>()
  const state: Ref<'selection' | 'loading' | 'done'> = ref('selection')
  const result = ref<Result | null>(null)

  const route = useRoute()

  const contractToEdit = ref<PriceInformation | null>(null)
  const contractEditMethod = ref<'create' | 'edit'>('edit')
  const isFallback = ref<boolean>(false)
  const isSSO = computed(() => route.query.sso === 'true')
  const currentSubaccount = computed(() => route.params.subAccountId as string)

  // Emits
  const emit = defineEmits<{
    'user-added': []
    'update:state': [value: 'selection' | 'loading' | 'done']
    'licenses-changed': []
    'application-changed': []
    selected: [value: SoftwareLicense]
    unselected: [value: SoftwareLicense]
    selectedAll: [value: SoftwareLicense[]]
    unselectedAll: [value: SoftwareLicense[]]
  }>()

  // Props
  export type Props = {
    application: SoftwareOut
    softwareLicenses: SoftwareLicense[]
    license?: LicenceOut // Needed for edit mode
    user?: Account[] // Needed for add mode
    filter?: '' | 'ActiveAndEdit'
    subAccountId?: string | null // null is assignable to all subaccounts
    selectable?: boolean
    loading?: boolean
    showAddRow?: boolean
    hideHeader?: boolean
    licensesEditable?: boolean
    filterFlatrate?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    filter: '',
    selectable: true,
    loading: false,
    showAddRow: false,
    hideHeader: false,
    licensesEditable: false,
    filterFlatrate: false,
    subAccountId: null,
    license: undefined, // Needed for edit mode
    user: undefined, // Needed for add mode
  })

  const columns: Column<SoftwareLicense>[] = [
    {
      label: '',
      key: 'automated',
      width: '50px',
      sortable: false,
    },
    {
      label: i18n.t('license.license'),
      key: 'name',
      width: 2,
      loader: true,
      sortFn: (a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
    },
    {
      label: i18n.t('quota'),
      key: 'quota',
      width: 2,
      sortable: false,
    },
    {
      label: i18n.t('price'),
      key: 'price',
      width: 2,
      sortable: false,
    },
    {
      label: i18n.t('pricePerUser'),
      key: 'pricePerUser',
      width: 2,
      sortable: false,
    },

    {
      key: 'action',
      label: '',
      sortable: false,
      width: '50px',
    },
  ]

  const selection = computed(() => {
    if (!softwareLicenseTable.value) return []
    return softwareLicenseTable.value.selection as SoftwareLicense[]
  })

  // Table rows
  // Here we add the id to the license model (seems to be missing in the api)

  // Get the license models that are not already in the license
  const licensesToAdd = computed(() => {
    if (!props.license) return [] // Guard clause if no license is given (create new license)
    if (!props.license.software_licenses) return []
    const softwareLicenseKeys = Object.keys(props.license.software_licenses)
    if (!softwareLicenseTable.value) return []
    const selection: SoftwareLicense[] = softwareLicenseTable.value?.selection
    if (!selection) return []
    const selectionIds = selection.map((model) => model._id)
    const toAdd = selectionIds.filter((id) => !softwareLicenseKeys.includes(id))
    return toAdd
  })

  const licensesToDelete = computed(() => {
    if (!props.license) return [] // Guard clause if no license is given (create new license)
    if (!props.license.software_licenses) return []
    const softwareLicenseKeys = Object.keys(props.license.software_licenses)
    const selection = softwareLicenseTable.value?.selection
    if (!selection) return []
    const selectionIds = selection.map((model: SoftwareLicense) => model._id)
    const toDelete = softwareLicenseKeys.filter(
      (id) => !selectionIds.includes(id)
    )
    return toDelete
  })

  // Search related
  const searchQuery = ref('')
  const filteredLicenseModelRows = computed(() => {
    const licenses = props.softwareLicenses
    let filtered = licenses.map((l) => ({ ...l }))

    if (isSSO.value) {
      filtered = filtered.filter(
        (l) => l.sub_account_id === decodeURIComponent(currentSubaccount.value)
      )
    }

    if (props.selectable) {
      filtered = filtered.filter((l) => !l.not_editable_at_the_user)
    }

    if (showLicenseDrawerVisible.value && licenseToShow.value) {
      const updated = licenses.find((l) => l._id === licenseToShow.value?._id)
      if (updated) licenseToShow.value = updated
    }

    if (searchQuery.value) {
      const q = searchQuery.value.toLowerCase()
      filtered = filtered
        .map((l) => {
          const licenseMatch = Object.values(l).some(
            (val) => typeof val === 'string' && val.toLowerCase().includes(q)
          )

          const contracts = contractStore.getContract(l._id) || []
          const filteredContracts = contracts.filter((c) =>
            Object.values(c).some(
              (val) => typeof val === 'string' && val.toLowerCase().includes(q)
            )
          )

          return {
            ...l,
            filteredContracts: licenseMatch ? contracts : filteredContracts,
            match: licenseMatch || filteredContracts.length > 0,
          }
        })
        .filter((l) => l.match)
    } else {
      filtered = filtered.map((l) => ({
        ...l,
        filteredContracts: contractStore.getContract(l._id) || [],
      }))
    }

    return filtered
  })

  // external name column
  if (filteredLicenseModelRows.value.some((row) => row.external_name)) {
    columns.splice(2, 0, {
      label: i18n.t('externalName'),
      key: 'extName',
      width: 2,
      loader: true,
    })
  }

  const editMode = computed(() => {
    return props.license !== null
  })

  function execute() {
    switch (state.value) {
      case 'selection': // Add the license
        if (editMode.value) {
          updateLicense()
        } else {
          addLicense()
        }
        break
      case 'loading': // Do nothing
        break
    }
  }

  const licenseCosts = ref<Record<string, SoftwareLicenseCost>>({})

  async function fetchLicenseCost() {
    if (!props.application?._id) return

    await preferenceStore.getPreferences()

    licenseCosts.value =
      await SoftwareLicensesService.getSoftwareLicensesCostsApiV1SoftwareSoftwareLicensesCostsGet(
        {
          softwareId: props.application._id,
          targetCurrencyCode: preferenceStore.preferences?.currency_code,
        }
      )
  }

  function softwareLicenseSelectionToSoftwareLicenseMiniUpdateRecord() {
    const softwareLicenses: Record<string, SoftwareLicenseMiniUpdate | null> =
      {}
    selection.value.forEach((softwareLicense) => {
      softwareLicenses[softwareLicense._id] = {
        name: softwareLicense.name,
      }
    })
    licensesToDelete.value.forEach((softwareLicenseId) => {
      softwareLicenses[softwareLicenseId] = null
    })
    return softwareLicenses
  }

  // Send request to add license
  function addLicense() {
    if (!props.user) return
    state.value = 'loading'
    const software_licenses =
      softwareLicenseSelectionToSoftwareLicenseMiniUpdateRecord()

    for (const _single_user of props.user) {
      AccountStore.createAccount({
        status: AccountStatus.ACTIVE,
        email: _single_user?.email,
        name: _single_user?.name,
        software_id: props.application._id,
        software_licenses: software_licenses as Record<
          string,
          SoftwareLicenseMini_Input
        >,
        sub_account_id: props.subAccountId!,
      })
        .then((response) => {
          if (!response) return
          if (!response.error) {
            result.value = {
              status: 'success',
              message: 'Nutzer erfolgreich hinzugefügt',
              response: response.data || undefined,
            }
            emit('user-added')
            emit('licenses-changed')
          } else if (response.error instanceof ApiError) {
            result.value = {
              status: 'error',
              message: response.error.body.detail.message,
            }
          } else
            result.value = {
              status: 'error',
              message: i18n.t('error.general.unknown_error.message'),
            }

          const softwareLicenseIds: string[] = Object.keys(
            response.data?.software_licenses || {}
          )
          applicationDetailsStore.updateQuota(softwareLicenseIds)
        })
        .finally(() => {
          state.value = 'done'
        })
    }
  }

  function updateLicense() {
    state.value = 'loading'
    if (!props.user) return
    if (!props.license?._id) return

    const requestValue: LicenceUpdateIn = {
      // Ignore linting next line, because the license is not null
      software_licenses:
        softwareLicenseSelectionToSoftwareLicenseMiniUpdateRecord(),
    }

    AccountStore.updateAccount(props.license._id, requestValue)
      .then((response) => {
        if (!response.error) {
          result.value = {
            status: 'success',
            message: 'Lizenzen erfolgreich gespeichert',
            response: response.data || undefined,
          }
          emit('user-added')
          emit('licenses-changed')
        } else if (response.error instanceof ApiError) {
          result.value = {
            status: 'error',
            message: response.error.body.detail.message,
          }
        } else {
          result.value = {
            status: 'error',
            message: i18n.t('error.general.unknown_error.message'),
          }
        }

        const afterIds = Object.keys(
          requestValue.software_licenses || {}
        ).filter((id) => requestValue.software_licenses?.[id] !== null)
        const beforeIds = Object.keys(props.license?.software_licenses || {})

        applicationDetailsStore.updateQuota(afterIds, beforeIds)
      })
      .finally(() => {
        state.value = 'done'
      })
  }

  function deselectLicenseModel(softwareLicense: string) {
    softwareLicenseTable.value?.deselectById(softwareLicense)
  }

  function handleCellClick(
    _: unknown,
    columnKey: string,
    row: SoftwareLicense
  ) {
    if (columnKey === 'action') return

    if (props.licensesEditable) {
      handleShowLicenseModelClick(row)
    }
  }

  function handleEditLicenseModelClick(row: SoftwareLicense) {
    licenseEditDialogVisible.value = true
    licenseToEdit.value = row
  }

  function handleContractRowClick(
    row: SoftwareLicense,
    contract: PriceInformation
  ) {
    licenseToEdit.value = row
    contractToEdit.value = contract
    contractEditMethod.value = 'edit'
    isFallback.value = contract._id === row.contract_configuration?.fallback
    contractEditDialogVisible.value = true
  }

  function handleShowLicenseModelClick(row: SoftwareLicense) {
    showLicenseDrawerVisible.value = true
    licenseToShow.value = row
  }

  function handleContractDialogSaved() {
    contractEditDialogVisible.value = false
    contractToEdit.value = null
    isFallback.value = false
    contractEditMethod.value = 'edit'
    emit('licenses-changed')
  }

  function handleContractDialogClosed() {
    contractEditDialogVisible.value = false
    contractToEdit.value = null
    isFallback.value = false
    contractEditMethod.value = 'edit'
  }

  async function handleLicenseModelSaved() {
    // Refetch software
    emit('application-changed')
    emit('licenses-changed')

    // Close dialog
    licenseEditDialogVisible.value = false

    // update drawer value
  }

  async function handleDeleteLicenseModel() {
    if (!licenseToDelete.value) return
    await deleteSoftwareLicense(licenseToDelete.value._id)
    emit('licenses-changed')
    deleteLicenseDialogVisible.value = false
    showLicenseDrawerVisible.value = false
  }

  function handleDeleteLicenseModelClick(row: SoftwareLicense) {
    deleteLicenseDialogVisible.value = true
    licenseToDelete.value = row
  }

  // Select all license models that are already in the license
  onMounted(() => {
    if (!props.license?.software_licenses) return
    const softwareLicenseKeys = Object.keys(props.license.software_licenses)
    softwareLicenseTable.value?.selectByIds(softwareLicenseKeys)
  })

  watch(
    () => props.softwareLicenses,
    (newLicenses, oldLicenses) => {
      if (newLicenses && newLicenses.length > 0) {
        fetchLicenseCost()
      }
    },
    { immediate: true, deep: true }
  )

  // Watch for changes in props.license
  watch(
    () => props.license,
    () => {
      if (!props.license?.software_licenses) return
      softwareLicenseTable.value?.resetSelection()
      const softwareLicenseKeys = Object.keys(props.license.software_licenses)
      softwareLicenseTable.value?.selectByIds(softwareLicenseKeys)
    }
  )

  watch(
    () => applicationDetailsStore.licences,
    (licenses) => {
      if (licenses?.length) {
        applicationDetailsStore.loadAllQuotas(licenses)
      }
    },
    { immediate: true }
  )

  defineExpose({
    execute: execute,
    state: state,
    result: result,
    selection: selection,
    deselectLicenseModel: deselectLicenseModel,
    filteredLicenseModelRows: filteredLicenseModelRows,
    table: softwareLicenseTable,
    openAddLicense,
  })

  async function openAddLicense() {
    licenseCreateDialogVisible.value = true
  }
</script>

<style lang="css" scoped>
  .license-table {
    max-height: calc(100% - 30px);
  }

  .contract-row {
    cursor: pointer;
    transition: background-color 0.15s ease;
  }

  .contract-row:hover,
  .contract-row:focus-visible {
    background-color: var(--sm-surface-hover, rgba(148, 163, 184, 0.12));
  }
</style>
